/**
 * aiDrawing.js — Natural Language → DCEL mutation engine
 *
 * Architecture:
 *   1. User types "3BHK flat 1200 sqft" or "draw a ball bearing OD=50 ID=20 6 balls"
 *   2. buildAIPrompt() wraps user intent with DCEL schema context
 *   3. callAIDrawEndpoint() sends to the ai-draw Edge Function (keeps API key server-side)
 *   4. parseAIMutations() converts Claude's JSON response into real mutations
 *   5. executeMutations() applies them to current state in sequence
 *
 * Claude returns a structured JSON array of mutation calls:
 *   [
 *     { fn: "LINE",  args: { x1:0, y1:0, x2:3000, y2:0, thickness:230 } },
 *     { fn: "RECT",  args: { x:0, y:0, w:3000, h:2000, thickness:230 } },
 *     { fn: "CIRCLE",args: { cx:0, cy:0, r:500 } },
 *     { fn: "ARC",   args: { cx:0, cy:0, r:500, startDeg:0, endDeg:180 } },
 *     { fn: "ROOM",  args: { faceIndex:0, type:"BEDROOM", name:"Master Bedroom" } },
 *     { fn: "OPENING", args: { edgeIndex:0, t:0.5, width:900, height:2100, type:"door" } },
 *     { fn: "LABEL", args: { x:1500, y:1000, text:"Living Room" } },
 *   ]
 */

import { mutAddCircle, mutAddArc, mutAddArc3Pt, mutAddPolyline } from "./primitives";
import { mutAddPolygon } from "./editMutationsV7";
import { mkId } from "../utils/coords";
import { Geo } from "../utils/geo";

// ─── Schema context sent to Claude ───────────────────────────────────────────

const DCEL_SCHEMA_CONTEXT = `
You are a CAD drawing assistant for DPL Jade. Convert the user's drawing request into a JSON array of mutation commands.

COORDINATE SYSTEM:
- All measurements in mm (millimetres)
- Origin (0,0) is top-left
- X increases rightward, Y increases downward
- Standard wall thickness: 230mm (brick), 150mm (partition), 100mm (glass)

AVAILABLE COMMANDS:
LINE   { x1, y1, x2, y2, thickness?, height?, materialId? }
       materialId: "BRICK"|"AAC"|"PARTITION"|"GLASS"|"RC"|"HOLLOW"
RECT   { x, y, w, h, thickness?, height?, materialId? }
       Creates 4 walls as a closed rectangle
CIRCLE { cx, cy, r, layerId? }
       layerId: "walls"|"structural"|"furniture"|"plumbing"|"electrical"
ARC    { cx, cy, r, startDeg, endDeg, layerId? }
POLYGON { cx, cy, r, sides, rotationDeg? }
POLYLINE { pts: [{x,y},...], closed?, layerId? }
OPENING { x1, y1, x2, y2, t, width, height, sillHeight?, type? }
         t = position along wall 0.0-1.0 from (x1,y1)
         type: "door"|"window"|"sliding"|"french"
ROOM   { x, y, type, name? }
       x,y = point inside the room polygon
       type: "LIVING"|"BEDROOM"|"KITCHEN"|"BATHROOM"|"TOILET"|"CORRIDOR"|"STORE"|"BALCONY"
LABEL  { x, y, text }
GRID   { axis, position, label }
       axis: "x"|"y", position in mm

DRAWING CONVENTIONS:
- Standard room heights: 2700-3000mm
- Door width: 900mm, height: 2100mm
- Window: 1200mm wide, 1200mm high, sill 900mm
- Bedroom: min 9 sqm, Living: min 15 sqm
- Standard 2BHK: 65-80 sqm, 3BHK: 100-120 sqm
- 1 sqm = 1,000,000 sqm² = 1000×1000mm

MECHANICAL/PRECISION DRAWINGS:
- For ball bearings, gears, etc: use CIRCLE and ARC
- Standard bearing shaft diameters: 10,12,15,17,20,25,30,35,40,45,50mm
- Ball bearing: inner race, outer race, balls (polar array), cage arcs

RESPONSE FORMAT:
Return ONLY a valid JSON array. No explanation, no markdown, no code fences.
Example: [{"fn":"RECT","args":{"x":0,"y":0,"w":5000,"h":4000,"thickness":230}}]
`.trim();

// ─── Build prompt ─────────────────────────────────────────────────────────────

export function buildAIPrompt(userRequest, currentState) {
  const nodeCount = Object.keys(currentState?.nodes || {}).length;
  const edgeCount = Object.keys(currentState?.edges || {}).length;
  const mode      = currentState?.drawingMode || "architectural";

  const context = nodeCount > 0
    ? `\nCurrent drawing has ${nodeCount} nodes and ${edgeCount} edges in ${mode} mode. Place new geometry to complement what exists, or draw standalone if requested.`
    : `\nCanvas is empty. Start from (0,0).`;

  return DCEL_SCHEMA_CONTEXT + context + `\n\nUser request: ${userRequest}`;
}

// ─── Call Edge Function (keeps API key server-side) ───────────────────────────

export async function callAIDrawEndpoint(prompt, supabaseSession) {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) throw new Error("SUPABASE_URL not configured");

  const res = await fetch(`${supabaseUrl}/functions/v1/ai-draw`, {
    method: "POST",
    headers: {
      "Content-Type":  "application/json",
      "Authorization": `Bearer ${supabaseSession?.access_token || ""}`,
      "apikey":        process.env.REACT_APP_SUPABASE_ANON_KEY || "",
    },
    body: JSON.stringify({ prompt }),
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`AI draw failed: ${err}`);
  }

  const data = await res.json();
  return data.mutations; // array of { fn, args }
}

// ─── Execute mutations ────────────────────────────────────────────────────────

/**
 * applyAIMutations(mutations, st, commit)
 *
 * Converts Claude's mutation JSON into real DCEL state changes.
 * Returns the updated state (does NOT call commit — caller decides).
 */
export function applyAIMutations(mutations, st) {
  if (!Array.isArray(mutations) || mutations.length === 0) return st;

  let s = st;
  const createdEdges = []; // track in order for OPENING references

  for (const mut of mutations) {
    try {
      s = applySingleMutation(mut, s, createdEdges);
    } catch (e) {
      console.warn(`[AI] Skipped mutation ${mut.fn}:`, e.message);
    }
  }

  return s;
}

function applySingleMutation(mut, st, createdEdges) {
  const { fn, args } = mut;
  if (!fn || !args) return st;

  switch (fn.toUpperCase()) {

    case "LINE": {
      const { x1, y1, x2, y2, thickness = 230, height = 3000, materialId = "BRICK" } = args;
      return addWallEdge(x1, y1, x2, y2, thickness, height, materialId, st, createdEdges);
    }

    case "RECT": {
      const { x, y, w, h, thickness = 230, height = 3000, materialId = "BRICK" } = args;
      let s = st;
      s = addWallEdge(x,   y,   x+w, y,   thickness, height, materialId, s, createdEdges);
      s = addWallEdge(x+w, y,   x+w, y+h, thickness, height, materialId, s, createdEdges);
      s = addWallEdge(x+w, y+h, x,   y+h, thickness, height, materialId, s, createdEdges);
      s = addWallEdge(x,   y+h, x,   y,   thickness, height, materialId, s, createdEdges);
      return s;
    }

    case "CIRCLE": {
      const { cx, cy, r, layerId = "walls" } = args;
      return mutAddCircle(cx, cy, r, layerId, st);
    }

    case "ARC": {
      const { cx, cy, r, startDeg, endDeg, layerId = "walls" } = args;
      const startAngle = startDeg * Math.PI / 180;
      const endAngle   = endDeg   * Math.PI / 180;
      return mutAddArc(cx, cy, r, startAngle, endAngle, layerId, st);
    }

    case "POLYGON": {
      const { cx, cy, r, sides, rotationDeg = 0, layerId = "walls" } = args;
      return mutAddPolygon(cx, cy, r, sides, rotationDeg, layerId, st);
    }

    case "POLYLINE": {
      const { pts, closed = false, layerId = "walls" } = args;
      if (!pts || pts.length < 2) return st;
      return mutAddPolyline(pts, closed, false, layerId, st);
    }

    case "OPENING": {
      const { x1, y1, x2, y2, t, width = 900, height = 2100, sillHeight = 0, type = "door" } = args;
      // Find the edge closest to the given line segment
      const targetEdge = findClosestEdge(x1, y1, x2, y2, st) ||
                         createdEdges[createdEdges.length - 1];
      if (!targetEdge) return st;
      const opId = mkId("op");
      const openings = {
        ...(st.openings || {}),
        [opId]: {
          id: opId, edgeId: targetEdge,
          t: Math.max(0.05, Math.min(0.95, t)),
          width, height, sillHeight,
          type, side: "left",
        },
      };
      return { ...st, openings };
    }

    case "ROOM": {
      const { x, y, type = "ROOM", name = "" } = args;
      // Find the face containing this point
      const faceId = findFaceContaining(x, y, st);
      if (!faceId) return st;
      const rooms = { ...(st.rooms || {}), [faceId]: { type, name } };
      return { ...st, rooms };
    }

    case "LABEL": {
      const { x, y, text } = args;
      const annId = mkId("ann");
      const annotations = {
        ...(st.annotations || {}),
        [annId]: { id: annId, x, y, text: String(text || ""), layerId: "annotations", style: "text" },
      };
      return { ...st, annotations };
    }

    case "GRID": {
      const { axis, position, label } = args;
      const glId = mkId("gl");
      const gridLines = {
        ...(st.gridLines || {}),
        [glId]: { id: glId, axis, position, label: String(label || "") },
      };
      return { ...st, gridLines };
    }

    default:
      console.warn(`[AI] Unknown mutation: ${fn}`);
      return st;
  }
}

// ─── DCEL wall creation helper ────────────────────────────────────────────────

function addWallEdge(x1, y1, x2, y2, thickness, height, materialId, st, createdEdges) {
  const len = Math.sqrt((x2-x1)**2 + (y2-y1)**2);
  if (len < 1) return st;

  const nAId = mkId("n"), nBId = mkId("n");
  const heAB = mkId("he"), heBA = mkId("he"), eId = mkId("e");

  const nodes = {
    ...st.nodes,
    [nAId]: { id: nAId, x: Math.round(x1), y: Math.round(y1), halfEdgeIds: [heAB] },
    [nBId]: { id: nBId, x: Math.round(x2), y: Math.round(y2), halfEdgeIds: [heBA] },
  };
  const halfEdges = {
    ...st.halfEdges,
    [heAB]: { id: heAB, originId: nAId, twinId: heBA, nextId: null, faceId: null },
    [heBA]: { id: heBA, originId: nBId, twinId: heAB, nextId: null, faceId: null },
  };
  const edges = {
    ...st.edges,
    [eId]: { id: eId, halfEdgeA: heAB, halfEdgeB: heBA, thickness, height, materialId, layerId: "walls", constraintIds: [] },
  };

  createdEdges.push(eId);
  return { ...st, nodes, halfEdges, edges };
}

// ─── Spatial helpers ──────────────────────────────────────────────────────────

function findClosestEdge(x1, y1, x2, y2, st) {
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  let best = null, bestD = Infinity;
  for (const edge of Object.values(st.edges || {})) {
    const ab = st.halfEdges[edge.halfEdgeA], ba = st.halfEdges[edge.halfEdgeB];
    if (!ab || !ba) continue;
    const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
    if (!nA || !nB) continue;
    const emx = (nA.x + nB.x) / 2, emy = (nA.y + nB.y) / 2;
    const d = Math.sqrt((mx - emx)**2 + (my - emy)**2);
    if (d < bestD) { bestD = d; best = edge.id; }
  }
  return best;
}

function findFaceContaining(px, py, st) {
  for (const [faceId, face] of Object.entries(st.faces || {})) {
    if (!face.loop) continue;
    const pts = face.loop.map(hid => {
      const he = st.halfEdges[hid];
      return he ? st.nodes[he.originId] : null;
    }).filter(Boolean);
    if (pts.length < 3) continue;
    if (pointInPolygon(px, py, pts)) return faceId;
  }
  return null;
}

function pointInPolygon(px, py, pts) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
    if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi))
      inside = !inside;
  }
  return inside;
}
